// (d) Typed descriptive answer. React Hook Form + Zod, min 10 chars, live count.
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const schema = z.object({
  answer: z.string().trim().min(1, 'Please enter your answer.'),
});
type FormValues = z.infer<typeof schema>;

interface TypedInputProps {
  disabled?: boolean;
  submitting?: boolean;
  initialValue?: string;
  onValueChange?: (answer: string) => void;
  onSubmit: (answer: string) => void;
}

export function TypedInput({
  disabled,
  submitting,
  initialValue = '',
  onValueChange,
  onSubmit,
}: TypedInputProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { answer: initialValue },
  });

  // Server hydration can arrive after the panel mounts. Restoring the most
  // recent typed attempt lets a student edit it after a wrong grade or reload.
  useEffect(() => {
    reset({ answer: initialValue });
  }, [initialValue, reset]);

  const value = watch('answer') ?? '';

  useEffect(() => {
    onValueChange?.(value);
  }, [onValueChange, value]);

  return (
    <form onSubmit={handleSubmit((v) => onSubmit(v.answer.trim()))}>
      <textarea
        {...register('answer')}
        disabled={disabled}
        rows={6}
        placeholder="Write your full working and answer here…"
        className="w-full resize-y rounded-[var(--control-radius)] border border-line p-3 text-ink outline-none transition focus:border-forest focus:ring-2 focus:ring-slate-200 disabled:opacity-60"
        aria-invalid={Boolean(errors.answer)}
      />
      <div className="mt-1 flex items-center justify-between text-xs">
        <span style={{ color: errors.answer ? 'var(--danger)' : undefined }} className={errors.answer ? '' : 'text-slate-400'}>
          {errors.answer?.message ?? 'Show your method for full marks.'}
        </span>
        <span className="text-slate-400">{value.trim().length} chars</span>
      </div>

      <button
        type="submit"
        disabled={disabled || submitting || !isValid}
        className="mt-4 w-full rounded-xl bg-slate-900 px-4 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {submitting ? 'Submitting…' : 'Submit answer'}
      </button>
    </form>
  );
}
